import type { GlobalState } from '@affine/core/modules/storage';
import { LiveData } from '@toeverything/infra';
import { catchError, map, of } from 'rxjs';

/**
 * A UI preference kept in the global state (local storage on the web). Stored values are untrusted:
 * `parse` validates them and supplies the default for a missing, malformed or unreadable value.
 */
export function watchGlobalPreference<T>(
  globalState: GlobalState,
  key: string,
  parse: (value: unknown) => T
): LiveData<T> {
  let initial: unknown;
  try {
    initial = globalState.get<unknown>(key);
  } catch (error) {
    console.error(`Unreadable UI preference ${key}`, error);
  }
  return LiveData.from(
    globalState.watch<unknown>(key).pipe(
      map(parse),
      catchError((error: unknown) => {
        console.error(`Unreadable UI preference ${key}`, error);
        return of(parse(undefined));
      })
    ),
    parse(initial)
  );
}

/** Stores a UI preference; a storage that refuses it (quota, private mode) keeps the old one. */
export function storeGlobalPreference(
  globalState: GlobalState,
  key: string,
  value: unknown
) {
  try {
    globalState.set(key, value);
  } catch (error) {
    console.error(`UI preference ${key} not stored`, error);
  }
}
